import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import {
  BillingContractRequestError,
  type BillingCadenceMonths,
  type BillingContract,
  type BillingContractTerms,
} from '../../../api/onboardingContracts'
import { useOnboardingTeamLookup } from '../../../api/hooks'
import {
  useBillingContracts,
  useCreateBillingContract,
  useUpdateBillingContract,
} from '../../../hooks/useOnboardingContracts'
import { trackBillingContractAction } from '../../../analytics/interactionAnalytics'
import { INPUT_CLASS, SECONDARY_BUTTON_CLASS } from './ui'

const CADENCES: readonly BillingCadenceMonths[] = [1, 3, 6, 12]
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/

interface EditorState {
  kind: 'create' | 'edit' | 'end'
  contractId: string | null
  hasClosedPeriod: boolean
  activeOpenEndedEdit: boolean
  terms: BillingContractTerms
}

function cadence(value: string): BillingCadenceMonths {
  const parsed = Number(value)
  return parsed === 3 || parsed === 6 || parsed === 12 ? parsed : 1
}

function todayInTimeZone(timezone: string): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date())
  const value = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value ?? ''
  return `${value('year')}-${value('month')}-${value('day')}`
}

function statusOf(contract: BillingContract): 'ended' | 'active' | 'scheduled' {
  const today = todayInTimeZone(contract.timezone)
  if (contract.endsOn && contract.endsOn <= today) return 'ended'
  if (contract.startsOn > today) return 'scheduled'
  return 'active'
}

function termsOf(contract: BillingContract): BillingContractTerms {
  return {
    startsOn: contract.startsOn,
    endsOn: contract.endsOn,
    periodMonths: contract.periodMonths,
    timezone: contract.timezone,
  }
}

function contractErrorKey(error: Error): string {
  if (!(error instanceof BillingContractRequestError)) return 'unknown'
  switch (error.code) {
    case 'billing_contract_overlap':
    case 'contract_terms_closed':
    case 'stamped_charge_orphaned':
    case 'closed_period_orphaned':
    case 'invalid_contract_cadence':
    case 'invalid_contract_dates':
    case 'invalid_contract_timezone':
    case 'team_not_postpaid':
      return error.code
    default:
      return 'unknown'
  }
}

export default function BillingContractManager() {
  const { t } = useTranslation('pages')
  const [email, setEmail] = useState('')
  const [lookupEmail, setLookupEmail] = useState<string | null>(null)
  const [teamId, setTeamId] = useState<string | null>(null)
  const [editor, setEditor] = useState<EditorState | null>(null)
  const [actionError, setActionError] = useState<string | null>(null)

  const lookup = useOnboardingTeamLookup(lookupEmail)
  const selectedTeam = lookup.data?.teams.find((team) => team.team_id === teamId) ?? null
  const contracts = useBillingContracts(
    selectedTeam?.credit_billing_mode === 'postpaid' ? teamId : null,
  )
  const createContract = useCreateBillingContract()
  const updateContract = useUpdateBillingContract()
  const pending = createContract.isPending || updateContract.isPending
  const ordered = useMemo(
    () => [...(contracts.data ?? [])].sort((left, right) => left.startsOn.localeCompare(right.startsOn)),
    [contracts.data],
  )

  const lookupNow = () => {
    if (!EMAIL_RE.test(email.trim())) return
    setLookupEmail(email.trim().toLowerCase())
    setTeamId(null)
    setEditor(null)
    setActionError(null)
  }

  const openCreate = () => {
    setEditor({
      kind: 'create',
      contractId: null,
      hasClosedPeriod: false,
      activeOpenEndedEdit: false,
      terms: {
        startsOn: '',
        endsOn: null,
        periodMonths: 1,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      },
    })
    setActionError(null)
  }

  const openEdit = (contract: BillingContract, kind: 'edit' | 'end') => {
    setEditor({
      kind,
      contractId: contract.contractId,
      hasClosedPeriod: contract.hasClosedPeriod,
      activeOpenEndedEdit:
        kind === 'edit' && statusOf(contract) === 'active' && contract.endsOn === null,
      terms: termsOf(contract),
    })
    setActionError(null)
  }

  const validationError = editor === null
    ? null
    : !editor.terms.startsOn || !editor.terms.timezone.trim()
      ? t('adminOnboarding.contracts.validationRequired')
      : editor.kind === 'end' && !editor.terms.endsOn
        ? t('adminOnboarding.contracts.validationEndRequired')
        : editor.terms.endsOn && editor.terms.endsOn <= editor.terms.startsOn
          ? t('adminOnboarding.contracts.validationDates')
          : null

  const submit = async () => {
    if (!editor || !teamId || validationError) return
    const action = editor.kind
    try {
      if (editor.kind === 'create') {
        await createContract.mutateAsync({ teamId, terms: editor.terms })
      } else if (editor.contractId) {
        await updateContract.mutateAsync({
          teamId,
          contractId: editor.contractId,
          terms: editor.terms,
        })
      }
      trackBillingContractAction({
        teamId,
        contractId: editor.contractId ?? undefined,
        action,
        ok: true,
      })
      setEditor(null)
      setActionError(null)
    } catch (error) {
      trackBillingContractAction({
        teamId,
        contractId: editor.contractId ?? undefined,
        action,
        ok: false,
      })
      setActionError(
        t(`adminOnboarding.contracts.error.${contractErrorKey(error instanceof Error ? error : new Error())}`),
      )
    }
  }

  return (
    <section className="flex flex-col gap-5" data-testid="billing-contract-manager">
      <div>
        <h2 className="text-lg font-semibold text-foreground">{t('adminOnboarding.contracts.title')}</h2>
        <p className="mt-1 text-sm text-muted-foreground">{t('adminOnboarding.contracts.description')}</p>
      </div>

      <label className="flex flex-col gap-1.5">
        <span className="text-sm font-medium text-foreground">{t('adminOnboarding.contracts.accountEmail')}</span>
        <div className="flex gap-2">
          <input
            type="email"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            onKeyDown={(event) => { if (event.key === 'Enter') lookupNow() }}
            className={`${INPUT_CLASS} flex-1`}
            data-testid="billing-contract-email"
          />
          <button type="button" disabled={!EMAIL_RE.test(email.trim()) || lookup.isFetching} onClick={lookupNow} className={SECONDARY_BUTTON_CLASS} data-testid="billing-contract-lookup">
            {lookup.isFetching ? t('adminOnboarding.contracts.lookingUp') : t('adminOnboarding.contracts.lookup')}
          </button>
        </div>
      </label>

      {lookup.isError && <p role="alert" className="text-sm text-destructive">{t('adminOnboarding.contracts.lookupFailed')}</p>}
      {lookup.data && !lookup.data.user_found && <p className="text-sm text-muted-foreground">{t('adminOnboarding.contracts.userNotFound')}</p>}
      {lookup.data?.user_found && lookup.data.teams.length === 0 && <p className="text-sm text-muted-foreground">{t('adminOnboarding.contracts.noTeams')}</p>}

      {lookup.data && lookup.data.teams.length > 0 && (
        <label className="flex flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">{t('adminOnboarding.contracts.teamLabel')}</span>
          <select
            value={teamId ?? ''}
            onChange={(event) => { setTeamId(event.target.value || null); setEditor(null); setActionError(null) }}
            className="h-9 rounded-lg border border-input-border bg-input-bg px-3 text-sm text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            data-testid="billing-contract-team-select"
          >
            <option value="">{t('adminOnboarding.contracts.teamPlaceholder')}</option>
            {lookup.data.teams.map((team) => <option key={team.team_id} value={team.team_id}>{team.name}</option>)}
          </select>
        </label>
      )}

      {selectedTeam?.credit_billing_mode !== 'postpaid' && selectedTeam && (
        <div className="rounded-xl border border-border bg-surface-0 p-4 text-sm text-muted-foreground" data-testid="billing-contract-prepaid-guidance">
          {t('adminOnboarding.contracts.prepaidGuidance')}
        </div>
      )}

      {selectedTeam?.credit_billing_mode === 'postpaid' && (
        <>
          <div className="flex items-center justify-between gap-3">
            <h3 className="text-base font-semibold text-foreground">{t('adminOnboarding.contracts.schedule')}</h3>
            <button type="button" onClick={openCreate} disabled={pending} className="h-9 rounded-lg bg-primary px-3 text-sm font-medium text-white hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/20 disabled:opacity-50" data-testid="billing-contract-add">
              {t('adminOnboarding.contracts.add')}
            </button>
          </div>

          {contracts.isLoading && <p role="status" className="text-sm text-muted-foreground">{t('adminOnboarding.contracts.loading')}</p>}
          {contracts.isError && (
            <div role="alert" className="flex items-center gap-3 text-sm text-destructive">
              <span>{t('adminOnboarding.contracts.loadFailed')}</span>
              <button type="button" onClick={() => void contracts.refetch()} className={SECONDARY_BUTTON_CLASS}>{t('adminOnboarding.contracts.retry')}</button>
            </div>
          )}
          {contracts.data?.length === 0 && <p className="rounded-xl border border-border bg-surface-0 p-4 text-sm text-muted-foreground">{t('adminOnboarding.contracts.empty')}</p>}

          <div className="flex flex-col gap-2" data-testid="billing-contract-schedule">
            {ordered.map((contract, index) => {
              const previous = ordered[index - 1]
              const gap = previous?.endsOn && previous.endsOn < contract.startsOn
              const status = statusOf(contract)
              return (
                <div key={contract.contractId} className="contents">
                  {gap && <div className="rounded-lg border border-dashed border-border px-3 py-2 text-xs text-muted-foreground" data-testid="billing-contract-gap">{t('adminOnboarding.contracts.gap', { start: previous.endsOn, end: contract.startsOn })}</div>}
                  <article className="rounded-xl border border-border bg-surface-0 p-4" data-testid={`billing-contract-${contract.contractId}`}>
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div>
                        <p className="text-sm font-medium text-foreground">{t(`adminOnboarding.contracts.status.${status}`)}</p>
                        <p className="mt-1 text-sm text-muted-foreground">{t('adminOnboarding.contracts.range', { start: contract.startsOn, end: contract.endsOn ?? t('adminOnboarding.contracts.noEnd') })}</p>
                        <p className="mt-1 text-xs text-text-muted">{t('adminOnboarding.contracts.terms', { months: contract.periodMonths, timezone: contract.timezone })}</p>
                        {contract.hasClosedPeriod && <p className="mt-2 text-xs text-warning">{t('adminOnboarding.contracts.locked')}</p>}
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={() => openEdit(contract, 'edit')} className={SECONDARY_BUTTON_CLASS}>{t('adminOnboarding.contracts.edit')}</button>
                        {status === 'active' && contract.endsOn === null && <button type="button" onClick={() => openEdit(contract, 'end')} className={SECONDARY_BUTTON_CLASS}>{t('adminOnboarding.contracts.end')}</button>}
                      </div>
                    </div>
                  </article>
                </div>
              )
            })}
          </div>
        </>
      )}

      {editor && (
        <form className="rounded-xl border border-border bg-surface-1 p-4" onSubmit={(event) => { event.preventDefault(); void submit() }} data-testid="billing-contract-editor">
          <h3 className="text-base font-semibold text-foreground">{t(`adminOnboarding.contracts.editor.${editor.kind}`)}</h3>
          {editor.hasClosedPeriod && editor.kind === 'edit' && <p className="mt-1 text-xs text-warning">{t('adminOnboarding.contracts.lockedHelp')}</p>}
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            <label className="flex flex-col gap-1.5"><span className="text-sm text-foreground">{t('adminOnboarding.contracts.startDate')}</span><input type="date" value={editor.terms.startsOn} disabled={editor.kind === 'end' || editor.hasClosedPeriod} onChange={(event) => setEditor({ ...editor, terms: { ...editor.terms, startsOn: event.target.value } })} className={INPUT_CLASS} data-testid="billing-contract-start-date" /></label>
            {!editor.activeOpenEndedEdit && <label className="flex flex-col gap-1.5"><span className="text-sm text-foreground">{t('adminOnboarding.contracts.endDate')}</span><input type="date" value={editor.terms.endsOn ?? ''} onChange={(event) => setEditor({ ...editor, terms: { ...editor.terms, endsOn: event.target.value || null } })} className={INPUT_CLASS} data-testid="billing-contract-end-date" /></label>}
            <label className="flex flex-col gap-1.5"><span className="text-sm text-foreground">{t('adminOnboarding.contracts.cadence')}</span><select value={editor.terms.periodMonths} disabled={editor.kind === 'end' || editor.hasClosedPeriod} onChange={(event) => setEditor({ ...editor, terms: { ...editor.terms, periodMonths: cadence(event.target.value) } })} className={INPUT_CLASS}>{CADENCES.map((months) => <option key={months} value={months}>{t('adminOnboarding.contracts.months', { count: months })}</option>)}</select></label>
            <label className="flex flex-col gap-1.5"><span className="text-sm text-foreground">{t('adminOnboarding.contracts.timezone')}</span><input value={editor.terms.timezone} disabled={editor.kind === 'end' || editor.hasClosedPeriod} onChange={(event) => setEditor({ ...editor, terms: { ...editor.terms, timezone: event.target.value } })} className={INPUT_CLASS} data-testid="billing-contract-timezone" /></label>
          </div>
          {validationError && <p className="mt-3 text-sm text-destructive">{validationError}</p>}
          {actionError && <p role="alert" className="mt-3 text-sm text-destructive" data-testid="billing-contract-action-error">{actionError}</p>}
          {editor.kind === 'end' && <p className="mt-3 text-sm text-warning">{t('adminOnboarding.contracts.endConfirm')}</p>}
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={() => setEditor(null)} className={SECONDARY_BUTTON_CLASS}>{t('adminOnboarding.contracts.cancel')}</button>
            <button type="submit" disabled={pending || validationError !== null} className="h-9 rounded-lg bg-primary px-3 text-sm font-medium text-white hover:bg-primary/90 disabled:opacity-50" data-testid="billing-contract-save">{pending ? t('adminOnboarding.contracts.saving') : t(`adminOnboarding.contracts.save.${editor.kind}`)}</button>
          </div>
        </form>
      )}
    </section>
  )
}
